import { Component } from '@theme/component';

const OPEN_EVENT = 'shoppable-card:open';

/**
 * A shoppable card: opens the quick-buy modal for a clicked pin and, on mobile,
 * toggles a product list panel. Only one card's panel is open at a time.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} toggle - The "+"/"X" button.
 * @property {HTMLElement} panel - The pins container / mobile list panel.
 *
 * @extends Component<Refs>
 */
export class ShoppableCardComponent extends Component {
  requiredRefs = ['toggle', 'panel'];

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(OPEN_EVENT, this.#handleOtherOpen);
    document.addEventListener('keydown', this.#handleKeydown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(OPEN_EVENT, this.#handleOtherOpen);
    document.removeEventListener('keydown', this.#handleKeydown);
  }

  get #isOpen() {
    return this.hasAttribute('open');
  }

  /**
   * Toggle the mobile product-list panel.
   * @returns {void}
   */
  togglePanel = () => {
    if (this.#isOpen) {
      this.#close(true);
    } else {
      this.#open();
    }
  };

  /** @returns {void} */
  #open() {
    this.setAttribute('open', '');
    this.refs.toggle.setAttribute('aria-expanded', 'true');
    // Close every other open card.
    document.dispatchEvent(new CustomEvent(OPEN_EVENT, { detail: { source: this } }));
  }

  /**
   * @param {boolean} [restoreFocus]
   * @returns {void}
   */
  #close(restoreFocus = false) {
    if (!this.#isOpen) return;
    this.removeAttribute('open');
    this.refs.toggle.setAttribute('aria-expanded', 'false');
    if (restoreFocus) this.refs.toggle.focus();
  }

  /**
   * @param {Event} event
   * @returns {void}
   */
  #handleOtherOpen = (event) => {
    const custom = /** @type {CustomEvent} */ (event);
    if (custom.detail?.source !== this) this.#close();
  };

  /**
   * @param {KeyboardEvent} event
   * @returns {void}
   */
  #handleKeydown = (event) => {
    if (event.key === 'Escape' && this.#isOpen) this.#close(true);
  };

  /**
   * Open the quick-buy modal for the clicked pin's product.
   * @param {MouseEvent} event
   * @returns {void}
   */
  openPin = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const pin = target?.closest('.shoppable-pin') ?? null;
    if (!pin) return;

    const quickAdd = /** @type {(HTMLElement & { handleClick: (e: MouseEvent) => void }) | null} */ (
      pin.querySelector('quick-add-component')
    );
    if (!quickAdd) return;

    event.preventDefault();
    this.#close();
    quickAdd.handleClick(new MouseEvent('click', { bubbles: true, cancelable: true }));
  };
}

customElements.define('shoppable-card', ShoppableCardComponent);
